import React, { useState, useContext } from 'react';

import { Icon, Box, LayoutContext } from '@lucid/components-react';
import { getI18nData, hasHeaderTag } from '@lucid/utils';
import { graphql, useStaticQuery } from 'gatsby';
import { mqMin } from '@lucid/theme';
import type { Lucid } from 'types/types';

interface Props {
  paragraphs: string[];
  hideArrows?: boolean;
}

const ParagraphPager = ({ paragraphs, hideArrows }: Props) => {
  const [activeParagraph, setActiveParagraph] = useState(0);

  const goToIndex = (i: number) => {
    setActiveParagraph(i);
  };

  const arrowClick = () => {
    setActiveParagraph(activeParagraph === paragraphs.length - 1 ? 0 : activeParagraph + 1);
  };

  const {
    language: { i18nKey },
  } = useContext(LayoutContext);
  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_template_aria_label_next_slide
                mktg_template_aria_label_slide_title
                mktg_template_aria_label_slide_index
              }
            }
          }
        }
      }
    `,
  );

  const i18n = getI18nData(i18nData, i18nKey);

  const getAriaHeadings = (paragraph: string, index: number) => {
    const ariaHeadingFromHTMLRegex = new RegExp(hasHeaderTag);
    const ariaHeading = ariaHeadingFromHTMLRegex.exec(paragraph);

    if (ariaHeading && ariaHeading[1]) {
      return i18n('mktg-template-aria-label-slide-title', {
        title: ariaHeading[1],
      });
    }
    return i18n('mktg-template-aria-label-slide-index', { index: index + 1 });
  };

  return (
    <Box>
      <Box
        css={(theme: Lucid.Theme) => [theme.shared.htmlParserStyles, { paddingRight: theme.space.s40 }]}
        dangerouslySetInnerHTML={{ __html: paragraphs[activeParagraph] }}
      />
      <ul
        css={(theme: Lucid.Theme) => ({
          listStyle: 'none',
          margin: `${theme.space.s24}px 0 ${theme.space.s16}px`,
          padding: 0,
          display: 'flex',
          justifyContent: 'center',
          [mqMin.tabletSmall]: {
            justifyContent: 'flex-start',
          },
        })}
      >
        {paragraphs.map((paragraph, i) => (
          <li
            key={`${paragraph}-${i}`}
            css={(theme: Lucid.Theme) => ({
              display: 'inline-block',
              margin: `0 0 ${theme.space.s16}px`,
            })}
          >
            <button
              aria-label={getAriaHeadings(paragraph, i)}
              css={(theme: Lucid.Theme) => [
                {
                  background: theme.colors.white,
                  border: `1px solid ${theme.colors.coolGrey[100]}`,
                  borderRadius: '50%',
                  height: '0.75rem',
                  margin: `0 ${theme.space.s4}px 0 0`,
                  padding: 0,
                  width: '0.75rem',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                },
                i === activeParagraph && {
                  background: theme.colors.coolGrey[100],
                },
              ]}
              onClick={() => goToIndex(i)}
              type="button"
            />
          </li>
        ))}
      </ul>
      {!hideArrows && (
        <button
          aria-label={i18n('mktg-template-aria-label-next-slide')}
          css={{
            alignItems: 'center',
            justifyContent: 'flex-end',
            background: 'transparent',
            border: 0,
            content: '""',
            display: 'flex',
            height: '45px',
            position: 'absolute',
            right: 0,
            top: 'calc(50% - 25px)',
            width: '35px',
            '&:hover': {
              cursor: 'pointer',
            },
          }}
          onClick={() => arrowClick()}
          type="button"
        >
          <Icon type="CarotRight" />
        </button>
      )}
    </Box>
  );
};

export default ParagraphPager;
